import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { LatexRenderer } from '../../components/ui/LatexRenderer';
import { shuffleOptions } from '../../utils/deterministicShuffle';
import { ASSESSMENT_QUESTION_TEXT_ID } from './AssessmentQuestionCard';
interface AssessmentMCOptionsProps {
  questionId: string;
  options: string[];
  onSelect: (originalIndex: number) => void;
  selectedOriginalIndex: number | null;
}
const LABELS = ['A', 'B', 'C', 'D'];
export function AssessmentMCOptions({
  questionId,
  options,
  onSelect,
  selectedOriginalIndex
}: AssessmentMCOptionsProps) {
  const [shuffledOptions, setShuffledOptions] = useState<Array<{
    text: string;
    originalIndex: number;
  }>>([]);
  useEffect(() => {
    setShuffledOptions(shuffleOptions(options, questionId));
  }, [questionId, options]);
  const handleSelect = useCallback((index: number) => {
    if (shuffledOptions[index]) {
      onSelect(shuffledOptions[index].originalIndex);
    }
  }, [shuffledOptions, onSelect]);
  // Keyboard shortcuts (A, B, C, D of 1, 2, 3, 4)
  //
  // Twee bewakingen die ontbraken, allebei met hetzelfde gevolg: een antwoord
  // dat verandert zonder dat het kind dat doorheeft.
  //
  // 1. Modifiers. Zonder deze controle selecteerde Cmd+A ("alles selecteren")
  //    antwoord A. Een kind dat per ongeluk een toetscombinatie raakt, geeft
  //    dan een antwoord dat het nooit heeft gekozen.
  // 2. Open dialoog. Deze listener hangt aan `window` en blijft actief terwijl
  //    de "Toets stoppen?"-dialoog openstaat. Wie daar "Annuleren" wil kiezen
  //    en onderweg een lettertoets raakt, wijzigt het antwoord op de vraag
  //    eróchter — onzichtbaar, want de dialoog dekt hem af.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('[role="dialog"]')) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      const key = e.key.toUpperCase();
      let index = -1;
      if (['A', 'B', 'C', 'D'].includes(key)) {
        index = key.charCodeAt(0) - 65;
      } else if (['1', '2', '3', '4'].includes(key)) {
        index = parseInt(key) - 1;
      }
      if (index >= 0 && index < shuffledOptions.length) {
        handleSelect(index);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shuffledOptions, handleSelect]);
  return <div
  // `radiogroup` + `radio`: dit is precies wat het is — kies er één uit
  // vier. Als losse knoppen kondigde een schermlezer geen groep aan, gaf
  // geen positie ("2 van 4") en zei niet welke gekozen was; de selectie was
  // uitsluitend een gekleurde rand.
  role="radiogroup" aria-labelledby={ASSESSMENT_QUESTION_TEXT_ID} className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 w-full max-w-3xl mx-auto" data-id="element-761" data-mp="29zf94ia9jtnl">
      
      {shuffledOptions.map((option, index) => {
      const isSelected = option.originalIndex === selectedOriginalIndex;
      return <motion.button key={`${questionId}-${index}`} role="radio" aria-checked={isSelected} onClick={() => handleSelect(index)} whileHover={{
        scale: 1.01
      }} whileTap={{
        scale: 0.98
      }} className={`
              relative flex items-center p-4 sm:p-5 rounded-2xl text-left transition-all duration-200 shadow-sm
              focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-400 focus-visible:ring-offset-2
              ${isSelected ? 'bg-indigo-50 ring-2 ring-indigo-600 shadow-md' : 'bg-white hover:shadow-md'}
            `} data-id="element-762" data-mp="3f06iwiwiydt3" data-mpi={index}>
            
            {/* CITO-style Label */}
            <div className={`
                flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-sm sm:text-base mr-4 transition-colors
                ${isSelected ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'}
              `} data-id="element-763" data-mp="bk7olub0x3qr">
              
              {LABELS[index]}
            </div>

            {/* Option Text */}
            <div className={`flex-1 text-fluid-base sm:text-fluid-lg font-medium ${isSelected ? 'text-indigo-950' : 'text-slate-700'}`} data-id="element-764" data-mp="1m27y54czaobp">
              
              <LatexRenderer content={option.text} data-id="element-765" data-mp="1i2h7r1rd6a7p" />
            </div>
          </motion.button>;
    })}
    </div>;
}